import { Res } from 'common/types/responses'
import { convertShippingToAddress } from 'common/types/utils'
import { Req } from 'common/types/requests'
import { service } from 'common/service'
import { transformPagination } from '@hoxtonmix/utils'
import { toParam } from '@hoxtonmix/utils'
import { getStore } from 'common/store'
import { createUbo, deleteUbo, updateUbo } from './useUbo'
import {
  createUboVeriff,
  deleteUboVeriff,
  updateUboVeriff,
} from './useUboVeriff'
import {
  MemberWithOptionalId,
  MemberVeriffWithOptionalId,
} from 'common/types/requests'

export const updateMembers = async (
  companyId: number,
  subscriptionId: number,
  membersToRemove: number[],
  membersVeriffToRemove: string[],
  members: MemberWithOptionalId[],
  membersVeriff: MemberVeriffWithOptionalId[],
) => {
  const store = getStore()

  const uboDeletes = membersToRemove.map((id) =>
    deleteUbo(store, {
      company_id: companyId,
      id,
      subscription_id: subscriptionId,
    }),
  )

  const uboVeriffDeletes = membersVeriffToRemove.map((id) =>
    deleteUboVeriff(store, {
      company_id: companyId,
      id,
      subscription_id: subscriptionId,
    }),
  )

  const uboUpdates = members.map((member) =>
    member.id
      ? updateUbo(store, {
          company_id: companyId,
          data: member,
          subscription_id: subscriptionId,
        })
      : createUbo(store, {
          company_id: companyId,
          data: member,
          subscription_id: subscriptionId,
        }),
  )

  const uboVeriffUpdates = membersVeriff.map((member) =>
    member.id
      ? updateUboVeriff(store, {
          company_id: companyId,
          data: member,
          subscription_id: subscriptionId,
        })
      : createUboVeriff(store, {
          company_id: companyId,
          data: member,
          subscription_id: subscriptionId,
        }),
  )

  await Promise.all([
    ...uboDeletes,
    ...uboVeriffDeletes,
    ...uboUpdates,
    ...uboVeriffUpdates,
  ])
}

export const subscriptionService = service
  .enhanceEndpoints({ addTagTypes: ['Subscription'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      createSubscription: builder.mutation<
        Res['subscriptions'],
        Req['createSubscription']
      >({
        invalidatesTags: [{ id: 'LIST', type: 'Subscription' }],
        queryFn: async (query, _1, _2, baseQuery) => {
          const { data, error } = await baseQuery({
            body: {
              ...query.subscription,
              members: [],
            },
            method: 'POST',
            url: `partners/company/${query.company_id}/subscriptions`,
          })
          await updateMembers(
            data.company_id,
            data.subscription_id,
            query.membersToRemove,
            query.membersVeriffToRemove,
            query.subscription.members ?? [],
            query.subscription.membersVeriff ?? [],
          )
          return { data, error }
        },
      }),
      getAllSubscriptions: builder.query<
        Res['subscriptions'],
        Req['getAllSubscriptions']
      >({
        providesTags: [{ id: 'LIST', type: 'Subscription' }],
        query: (q) => ({
          responseHandler: q.export ? 'text' : 'json',
          url: `partners/subscriptions?${toParam(q)}`,
        }),
        transformResponse: (res, _, req) => {
          if (req.export) {
            return res
          }
          return transformPagination(req, res)
        },
      }),
      getCompanySubscriptions: builder.query<
        Res['subscriptions'],
        Req['getCompanySubscriptions']
      >({
        providesTags: [{ id: 'LIST', type: 'Subscription' }],
        query: (q) => ({
          url: `partners/subscriptions?${toParam(q)}`,
        }),
        transformResponse: (res, _, req) => transformPagination(req, res),
      }),
      getSubscription: builder.query<
        Res['subscription'],
        Req['getSubscription']
      >({
        providesTags: (res) => [
          { id: res?.subscription_id, type: 'Subscription' },
        ],
        query: (query: Req['getSubscription']) => ({
          url: `partners/company/${query.company_id}/subscriptions/${query.id}`,
        }),
        transformResponse: (res) => {
          return {
            ...res,
            shipping_address: convertShippingToAddress(res.shipping_address),
          }
        },
      }),
      updateSubscription: builder.mutation<
        Res['updateSubscription'],
        Req['updateSubscription']
      >({
        invalidatesTags: (res) => [
          { id: 'LIST', type: 'Subscription' },
          { id: res?.subscription_id, type: 'Subscription' },
        ],
        queryFn: async (query, _1, _2, baseQuery) => {
          const queryBody = {
            ...query.subscription,
          }

          const { data, error } = await baseQuery({
            body: queryBody,
            method: 'PUT',
            url: `partners/company/${query.company_id}/subscriptions/${query.subscription.id}`,
          })

          await updateMembers(
            query.company_id,
            query.subscription.id,
            query.membersToRemove,
            query.membersVeriffToRemove,
            query.subscription.members ?? [],
            query.subscription.membersVeriff ?? [],
          )

          return { data, error }
        },
      }),
      // END OF ENDPOINTS
    }),
  })

export async function getAllSubscriptions(
  store: any,
  data: Req['getAllSubscriptions'],
  options?: Parameters<
    typeof subscriptionService.endpoints.getAllSubscriptions.initiate
  >[1],
) {
  return store.dispatch(
    subscriptionService.endpoints.getAllSubscriptions.initiate(data, options),
  )
}
export async function getCompanySubscriptions(
  store: any,
  data: Req['getCompanySubscriptions'],
  options?: Parameters<
    typeof subscriptionService.endpoints.getCompanySubscriptions.initiate
  >[1],
) {
  return store.dispatch(
    subscriptionService.endpoints.getCompanySubscriptions.initiate(
      data,
      options,
    ),
  )
}
export async function createSubscription(
  store: any,
  data: Req['createSubscription'],
  options?: Parameters<
    typeof subscriptionService.endpoints.createSubscription.initiate
  >[1],
) {
  return store.dispatch(
    subscriptionService.endpoints.createSubscription.initiate(data, options),
  )
}
export async function updateSubscription(
  store: any,
  data: Req['updateSubscription'],
  options?: Parameters<
    typeof subscriptionService.endpoints.updateSubscription.initiate
  >[1],
) {
  return store.dispatch(
    subscriptionService.endpoints.updateSubscription.initiate(data, options),
  )
}
export async function getSubscription(
  store: any,
  data: Req['getSubscription'],
  options?: Parameters<
    typeof subscriptionService.endpoints.getSubscription.initiate
  >[1],
) {
  return store.dispatch(
    subscriptionService.endpoints.getSubscription.initiate(data, options),
  )
}
// END OF FUNCTION_EXPORTS

export const {
  useCreateSubscriptionMutation,
  useGetAllSubscriptionsQuery,
  useGetCompanySubscriptionsQuery,
  useGetSubscriptionQuery,
  useUpdateSubscriptionMutation,
  // END OF EXPORTS
} = subscriptionService

/* Usage examples:
const { data, isLoading } = useGetAllSubscriptionsQuery({}) //get hook
const { data, isLoading } = useGetCompanySubscriptionsQuery({ company_id: 2 }) //get company hook
const [createSubscription, { isLoading, data, isSuccess }] = useCreateSubscriptionMutation() //create hook
subscriptionService.endpoints.getAllSubscriptions.select({})(store.getState()) //access data from any function
*/
